import { useEffect, useRef, useState } from "react";
import type { TextButtonSize } from "@wix/design-system";
import { useSpeechRecognition } from "../../../hooks/useSpeechRecognition";
import type { Designer } from "@wix/ambassador-picasso-editor-v1-designer/types";
import { useBiLogger } from "../../../bi/useBiLogger";
import { SESSION_STORAGE_KEYS, isMobile } from "@wix/picasso-common";
import { usePanorama, useTranslation } from "@wix/fe-essentials-standalone";
import { getDesignerName } from "../../../utils/designerModalUtils";
import { navigateToProjects } from "../../../utils/utils";
import { useAppContext } from "../Common/useAppContext";
import { RANDOM_DESIGNER_ID } from "../../../consts";

export const useLandingPage = (designers: Designer[]) => {
  const { t } = useTranslation();
  const { errorMonitor } = usePanorama();
  const { isLoggedIn, userDetails } = useAppContext();

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  const [prompt, setPrompt] = useState<string>("");
  const [generatingProject, setIsGeneratingProject] = useState(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDesignStudioModalOpen, setIsDesignStudioModalOpen] = useState(false);
  const [selectedDesignStudio, setSelectedDesignStudio] = useState<
    Designer | undefined
  >(undefined);
  const [sizeMapByElementType, setSizeMapByElementType] = useState<{
    [key: string]: any;
  }>({});
  const biLogger = useBiLogger();
  const handleDesignStudioSelected = (designStudio: Designer) => {
    setIsDesignStudioModalOpen(false);
    setSelectedDesignStudio(designStudio);
  };

  const errorReporter = errorMonitor({
    data: { tags: { feature: "landing-page" } },
  });
  const handleResize = () => {
    const isMobileView = isMobile();
    const textAreaButton: TextButtonSize = isMobileView ? "small" : "medium";
    const button: TextButtonSize = isMobileView ? "small" : "medium";
    const title = isMobileView ? "large" : "extraLarge";
    setSizeMapByElementType({ textAreaButton, button, title });
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!designers?.length) {
      errorReporter.reportError(new Error("Failed to load designers"));
    }
  }, [designers, errorReporter]);

  useEffect(() => {
    if (isLoggedIn && !userDetails) {
      errorReporter.reportError(new Error("Failed to load user details"));
    }
  }, [isLoggedIn, userDetails, errorReporter]);

  const setSuggestion = (suggestion: string) => {
    if (suggestion) {
      setPrompt(suggestion);
    }
  };

  const handleGenerateSiteClick = async () => {
    if (!prompt) return;
    biLogger.trackUserClickPicasso({ buttonName: "Generate Site" });
    setIsGeneratingProject(true);
    sessionStorage.setItem(SESSION_STORAGE_KEYS.PROMPT, prompt);
    const designerName = getDesignerName(selectedDesignStudio, t);
    if (selectedDesignStudio?.id) {
      const isWildCard = selectedDesignStudio.id === RANDOM_DESIGNER_ID;
      sessionStorage.setItem(SESSION_STORAGE_KEYS.DESIGNER_NAME, designerName);
      if (!isWildCard) {
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.DESIGNER_ID,
          selectedDesignStudio.id,
        );
      }
    }
    navigateToProjects();
  };

  const onFileAttachment = (files: string[]) => {
    setAttachmentPreviews(files);
  };

  const handleTranscript = (text: string) => {
    setPrompt((prev) => (prev ? `${prev} ${text}` : text));
  };

  const { toggleMicrophone, isListening, speechError, isMicPermissionDenied } =
    useSpeechRecognition({
      onTranscript: handleTranscript,
    });

  const handleDesignStudioClick = () => {
    biLogger.handleDesignStudioClick(selectedDesignStudio);
    setIsDesignStudioModalOpen(true);
  };

  const handleFileAttachmentClick = () => {
    biLogger.trackUserClickPicasso({ buttonName: "File Attachment" });
    fileInputRef.current?.click();
  };

  return {
    handleGenerateSiteClick,
    setPrompt,
    setIsGeneratingProject,
    prompt,
    generatingProject,
    fileInputRef,
    setSuggestion,
    attachmentPreviews,
    onFileAttachment,
    isDesignStudioModalOpen,
    setIsDesignStudioModalOpen,
    handleDesignStudioSelected,
    sizeMapByElementType,
    toggleMicrophone,
    isListening,
    speechError,
    isMicPermissionDenied,
    setSelectedDesignStudio,
    selectedDesignStudio,
    selectedDesignerName: getDesignerName(selectedDesignStudio, t),
    handleDesignStudioClick,
    handleFileAttachmentClick,
  };
};
